// Phone number validation with intl-tel-input
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/js/utils';

document.addEventListener('turbo:load', () => {
  initIntlTelephoneInput();
});

document.addEventListener('turbo:frame-render', () => {
  initIntlTelephoneInput();
});

document.addEventListener('turbo:frame-update', () => {
  initIntlTelephoneInput();
});

// intl-tel-input is pinned to ^18.1.5, whose bundled libphonenumber metadata
// predates Ukraine's 2024 regulator allocation of the `75` mobile prefix to
// Kyivstar, so `iti.isValidNumber()` rejects otherwise-valid numbers like
// +380 75 965 7344. Upstream metadata (v20+) already recognizes this prefix,
// but upgrading is a larger breaking migration, so we special-case it here.
function isKnownValidUkraineMobile(input, iti) {
  let country = iti.getSelectedCountryData();
  if (!country || country.dialCode !== '380') return false;

  let digits = input.value.replace(/\D/g, '');
  if (digits.startsWith('380')) digits = digits.slice(3);
  else if (digits.startsWith('0')) digits = digits.slice(1);

  return /^75\d{7}$/.test(digits);
}

function getE164Number(input, iti) {
  if (iti.isValidNumber()) return iti.getNumber();
  if (isKnownValidUkraineMobile(input, iti)) {
    let digits = input.value.replace(/\D/g, '');
    if (digits.startsWith('380')) digits = digits.slice(3);
    else if (digits.startsWith('0')) digits = digits.slice(1);
    return '+380' + digits;
  }
  return iti.getNumber();
}

export default function initIntlTelephoneInput() {
  let inputs = document.querySelectorAll('input[type=\'tel\']');
  if (inputs.length) {
    inputs.forEach((input) => {
      if (input.dataset.intlTelInputId != undefined) {
        return;
      }
      // Optional: creates a sibling hidden input. Prefer naming the tel input itself
      // (e.g. user[phone_number]) so signup still works when this JS fails to load.
      let fieldName = input.dataset.formName;
      let iti = intlTelInput(input, {
        initialCountry: 'auto',
        hiddenInput: fieldName || '',
        autoPlaceholder: 'polite',
        preferredCountries: ['IN', 'US'],
        geoIpLookup: function (callback) {
          $.get(
            'https://ipinfo.io?token=2790597761e5ab',
            function () {},
            'jsonp'
          ).always(function (resp) {
            let countryCode = resp && resp.country ? resp.country : '';
            callback(countryCode);
          });
        },
      });

      // Always promote the visible field to E.164 on submit so strong params see the
      // full number even when hiddenInput is unused or named differently.
      if (input.form) {
        input.form.addEventListener('submit', function () {
          if (iti.isValidNumber() || isKnownValidUkraineMobile(input, iti)) {
            input.value = getE164Number(input, iti);
          }
        });
      }

      let errorMap = [
        'Invalid number',
        'Invalid country code',
        'Too short phone number for country code',
        'Too long phone number for country code',
        'Invalid number',
      ];

      let msgContainer = document.createElement('div');

      let validMsg = document.createElement('p');
      validMsg.setAttribute('id', 'valid-msg');
      validMsg.classList.add(
        'd-none',
        'hidden',
        'text-success',
        'text-green-600',
        'fs-14',
        'text-sm',
        'mt-1'
      );
      validMsg.innerText = 'Valid Phone Number';

      let errorMsg = document.createElement('p');
      errorMsg.setAttribute('id', 'error-msg');
      errorMsg.classList.add(
        'd-none',
        'hidden',
        'text-danger',
        'text-red-600',
        'fs-14',
        'text-sm',
        'mt-1'
      );

      input.parentElement.after(msgContainer);
      msgContainer.appendChild(validMsg);
      msgContainer.appendChild(errorMsg);

      let submitBtn = input.form
        ? Array.from(input.form.elements).filter(function (element) {
          return element.type == 'submit';
        })[0]
        : null;

      let reset = function () {
        input.classList.remove('error');
        errorMsg.innerHTML = '';
        if (submitBtn) submitBtn.disabled = false;
        errorMsg.classList.add('d-none', 'hidden');
        validMsg.classList.add('d-none', 'hidden');
      };

      let checkPhoneNumber = function () {
        reset();
        if (input.value.trim()) {
          if (iti.isValidNumber() || isKnownValidUkraineMobile(input, iti)) {
            validMsg.classList.remove('d-none', 'hidden');
            if (submitBtn) submitBtn.disabled = false;
            let hiddenField = $(
              'input[type=\'hidden\'][name=\'' + fieldName + '\']'
            );
            hiddenField.val(getE164Number(input, iti));
            hiddenField.trigger('change');
          } else {
            input.classList.add('error');
            if (submitBtn) submitBtn.disabled = true;
            let errorCode = iti.getValidationError();
            errorMsg.innerHTML = errorMap[errorCode];
            errorMsg.classList.remove('d-none', 'hidden');
          }
        } else {
          // If field is empty, don't show any validation message
          if (submitBtn) submitBtn.disabled = false;
        }
      };

      let hasBlurred = false;

      input.addEventListener('blur', function () {
        hasBlurred = true;
        checkPhoneNumber();
      });

      // Target the specific input instead of all tel inputs
      $(input).on('keyup', function () {
        // Only validate on keyup AFTER the first blur
        if (hasBlurred) {
          checkPhoneNumber();
        }
      });

      input.addEventListener('countrychange', function () {
        // Only validate on country change AFTER the first blur
        if (hasBlurred) {
          checkPhoneNumber();
        }
      });
    });
  }
}
